import { useState } from 'react';
import { LINK_DELETE_REASON_MAX, Size, linkDeletePath, t, type LinkTarget } from '@bms2/shared';
import { AlertBanner, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { post } from './api';

/**
 * 🔒 Delete this link (T728; decision 1500): the window a link row's glyph
 * opens for the holder of `records.links.delete`. It names the link, says
 * that closing with an end date is the ordinary way out, and asks why the
 * link was a mistake; Delete link stays disabled until a reason is typed.
 *
 * 🔨 A refusal is the server's own sentence, naming what reads the link, and
 * the window stays open on it with the row untouched. On success the window
 * closes and the caller reads the page again, so the row leaves its tile.
 */
export function DeleteLinkModal({ link, apiBaseUrl = '', onCancel, onDeleted }: {
  link: LinkTarget;
  apiBaseUrl?: string;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const typed = reason.trim();

  const remove = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post(linkDeletePath(link.kind, link.id), { reason: typed }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onDeleted();
  };

  return (
    <Modal
      open
      title={t('link.delete.title')}
      {...(link.label ? { subtitle: link.label } : {})}
      dataModal="delete-link"
      onCancel={onCancel}
      actions={[
        { kind: 'primary', label: t('link.delete.confirm'), onAct: () => void remove(), disabled: busy || typed === '' || typed.length > LINK_DELETE_REASON_MAX },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      <p className={modalStyles.fieldLabel} data-delete-link-hint="true">{t('link.delete.hint')}</p>
      <TextInput size={Size.Large} label={t('link.delete.reason')} value={reason} onChange={setReason} required name="deleteLinkReason" disabled={busy} />
    </Modal>
  );
}
